import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import {
  ApiResponse,
  CurrentUserDetailsResponse,
} from '../../../core/models/api-response.model';
import { AlertifyService } from '../alertify/alertify.service';
import { StorageService } from '../storage/storage.service';

export type AccountType = 'USER' | 'ADMIN' | undefined;

export interface User {
  id: string;
  name: string;
  email: string;
  address: string;
  accountType: 'ADMIN' | 'USER';
  permissions: string[];
  roles: string[];
  companyAddress: string;
  mustChangePassword: boolean;
  lastLoginTime: string;
  totpEnabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly userSubject = new BehaviorSubject<Partial<User> | null>(
    null,
  );
  readonly user$ = this.userSubject.asObservable();

  constructor(
    private readonly router: Router,
    private readonly apiService: ApiService,
    private readonly alertifyService: AlertifyService,
    private readonly storageService: StorageService,
  ) {}

  get accountType(): AccountType {
    return this.userSubject.value?.accountType ?? 'USER';
    // return 'USER';
  }

  get roles(): string[] {
    return this.userSubject.value?.roles ?? [];
  }

  get rolesSet(): Set<string> {
    return new Set(this.roles);
  }

  get permissions(): string[] {
    return this.userSubject.value?.permissions ?? [];
  }

  get permissionSet(): Set<string> {
    return new Set(this.permissions);
  }

  get username(): string {
    return this.userSubject.value?.name ?? '';
  }

  get currentUser(): Partial<User> | null {
    return this.userSubject.value;
  }

  get isAuthenticated(): boolean {
    return !!this.currentUser;
  }

  hasRoles(roles: string[]): boolean {
    const rolesSet = this.rolesSet;
    return roles.every((r) => rolesSet.has(r));
  }

  hasAnyRole(roles: string[]): boolean {
    const rolesSet = this.rolesSet;
    return roles.some((r) => rolesSet.has(r));
  }

  hasPermissions(permissions: string[]): boolean {
    const permissionSet = this.permissionSet;
    return permissions.every((p) => permissionSet.has(p));
  }

  hasAnyPermission(permissions: string[]): boolean {
    const permissionSet = this.permissionSet;
    return permissions.some((p) => permissionSet.has(p));
  }

  // initializeUserData() {
  //   const user = this.storageService.retrieve(this.storageService.USER_KEY);
  //   if (!user) return;

  //   const parsedUser = JSON.parse(user) as User;
  //   this.userSubject.next(parsedUser);
  // }

  // checkSession(): boolean {
  //   this.initializeUserData();
  //   return !!this.currentUser;
  // }

  initializeUserData() {
    return this.fetchCurrentUserDetails().pipe(
      take(1),
      tap((value) => {
        if (value.success) {
          const data = value.data;
          this.userSubject.next({
            id: data.userId,
            accountType: data.account_type as AccountType,
            email: data.email,
            address: data.company_address,
            name: data.name,
            roles: data.roles,
            permissions: data.permissions,
            companyAddress: data.company_address,
            mustChangePassword: data.must_change_password,
            lastLoginTime: data.last_login_time,
            totpEnabled: data.totp_enabled,
          });
        } else {
          this.userSubject.next(null);
        }
      }),
      catchError((err) => {
        this.userSubject.next(null);
        return of(null);
      }),
    );
  }

  checkSession(): Observable<boolean> {
    return this.initializeUserData().pipe(map((value) => !!value));
  }

  login(user: Partial<User>): void {
    this.userSubject.next(user);
    // this.persistUserData(user);
    this.redirectUser(user);
  }

  logout(): void {
    this.apiService.post<ApiResponse>('auth/logout', {}).subscribe({
      next: (res) => {
        if (!res.success) {
          this.alertifyService.error(res.message || 'Failed to log out');
        } else {
          this.forceUserOut();
        }
      },
      error: (error) => {
        let errorMessage = this.apiService.getErrorMessage(error);
        this.alertifyService.error(errorMessage || 'Failed to log out');
      },
    });
  }

  forceUserOut() {
    this.userSubject.next(null);
    this.storageService.clearStorage();
    this.router.navigate(['auth', 'login']);
  }

  private fetchCurrentUserDetails(): Observable<
    ApiResponse<CurrentUserDetailsResponse>
  > {
    return this.apiService.get<ApiResponse<CurrentUserDetailsResponse>>('me');
  }

  private persistUserData(user: Partial<User>) {
    this.storageService.persist(
      this.storageService.USER_KEY,
      JSON.stringify(user),
    );
  }

  private redirectUser(user?: Partial<User>) {
    this.redirectUserByAccountType();
  }

  private redirectUserByAccountType(): void {
    if (!this.currentUser) return;
    switch (this.currentUser.accountType) {
      case 'ADMIN':
        this.router.navigate(['/admin']);
        break;
      default:
        this.router.navigate(['/user']);
    }
  }

  private redirectUserByRole(): void {
    const adminRolesSet = new Set(['ADMIN']);
    let hasAdminRole = false;

    for (const role of this.roles) {
      hasAdminRole = adminRolesSet.has(role);
      if (hasAdminRole) break;
    }

    if (hasAdminRole) {
      this.router.navigate(['/admin']);
    } else {
      this.router.navigate(['/user']);
    }
  }
}
